import React, { useState } from 'react';
import { 
  Megaphone, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Link,
  Search,
  Filter,
  CheckSquare,
  Square,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MarketingCampaign } from '../types';
import { getCountryNames } from '../data/countries';
import { debugLog } from '../services/debugConfig';

export const MarketingCampaignManager: React.FC = () => {
  const { 
    marketingCampaigns,
    partnerCampaigns,
    partners,
    createMarketingCampaign, 
    updateMarketingCampaign, 
    deleteMarketingCampaign 
  } = useAuth();

  // Debug logging
  debugLog('MarketingCampaignManager - Data:', {
    marketingCampaigns: marketingCampaigns?.length || 0,
    partnerCampaigns: partnerCampaigns?.length || 0,
    partners: partners?.length || 0,
    campaignDetails: marketingCampaigns?.map(c => ({ id: c.id, name: c.name, status: c.status }))
  });
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<MarketingCampaign | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    status: 'draft' as MarketingCampaign['status'],
    partner_campaign_ids: [] as string[],
    region: '', // Region will be auto-determined from selected partner campaigns
    locale: '' // Optional: Language/locale for the campaign (e.g., 'en-US', 'en-GB', 'fr-CA')
  });

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '',
      description: '',
      status: 'draft',
      partner_campaign_ids: [],
      region: '',
      locale: ''
    });
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMarketingCampaign({
        ...campaignForm,
        created_by: 'admin' // Will be handled by database default or current user
      });
      setShowCreateModal(false);
      resetCampaignForm();
    } catch (error) {
      console.error('Failed to create marketing campaign:', error);
      // Keep modal open on error so user can try again
    }
  };

  const handleUpdateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCampaign) {
      try {
        await updateMarketingCampaign(editingCampaign.id, campaignForm);
        setEditingCampaign(null);
        resetCampaignForm();
      } catch (error) {
        console.error('Failed to update marketing campaign:', error);
        // Keep modal open on error so user can try again
      }
    }
  };

  const startEditCampaign = (campaign: MarketingCampaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      description: campaign.description,
      status: campaign.status,
      partner_campaign_ids: [...campaign.partner_campaign_ids],
      region: campaign.region || '',
      locale: campaign.locale || ''
    });
  };

  const togglePartnerCampaign = (campaignId: string) => {
    const selectedCampaign = partnerCampaigns.find(pc => pc.id === campaignId);
    
    setCampaignForm(prev => {
      const isRemoving = prev.partner_campaign_ids.includes(campaignId);
      let newPartnerCampaignIds: string[];
      let newRegion = prev.region;
      
      if (isRemoving) {
        // Remove campaign
        newPartnerCampaignIds = prev.partner_campaign_ids.filter(id => id !== campaignId);
        
        // Update region based on remaining campaigns
        if (newPartnerCampaignIds.length === 0) {
          newRegion = '';
        } else {
          const remainingCampaigns = partnerCampaigns.filter(pc => 
            newPartnerCampaignIds.includes(pc.id)
          );
          newRegion = remainingCampaigns[0]?.region || '';
        }
      } else {
        // Add campaign - check region compatibility
        if (prev.region && selectedCampaign?.region !== prev.region) {
          alert(`Cannot add campaign from ${selectedCampaign?.region} region. Current marketing campaign is for ${prev.region} region only.`);
          return prev;
        }
        
        newPartnerCampaignIds = [...prev.partner_campaign_ids, campaignId];
        newRegion = selectedCampaign?.region || prev.region;
      }
      
      return {
        ...prev,
        partner_campaign_ids: newPartnerCampaignIds,
        region: newRegion
      };
    });
  };

  const selectAllCampaigns = () => {
    const availableCampaigns = getAvailablePartnerCampaigns();
    const firstCampaignRegion = availableCampaigns[0]?.region;
    
    setCampaignForm(prev => ({
      ...prev,
      partner_campaign_ids: availableCampaigns.map(pc => pc.id),
      region: firstCampaignRegion || prev.region
    }));
  };

  const deselectAllCampaigns = () => {
    setCampaignForm(prev => ({
      ...prev,
      partner_campaign_ids: [],
      region: ''
    }));
  };

  // Get partner campaigns filtered by region compatibility
  const getAvailablePartnerCampaigns = () => {
    const activeCampaigns = partnerCampaigns.filter(pc => pc.status === 'active');
    
    // If no region is selected yet, show all active campaigns
    if (!campaignForm.region) {
      return activeCampaigns;
    }
    
    // If region is selected, only show campaigns from the same region
    return activeCampaigns.filter(pc => pc.region === campaignForm.region);
  };

  const filteredCampaigns = marketingCampaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPartnerName = (partnerId: string) => {
    const partner = partners.find(p => p.id === partnerId);
    return partner?.name || 'Unknown Partner';
  };

  const getPublicUrl = (campaignId: string) => {
    if (!campaignId) {
      console.warn('Campaign ID is missing');
      return `${window.location.origin}/campaign/`;
    }
    return `${window.location.origin}/campaign/${campaignId}`;
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  // Early return if data is not loaded
  if (!marketingCampaigns || !partnerCampaigns || !partners) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading marketing campaigns...</p>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="space-y-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Marketing Campaign Manager</h2>
        <p className="text-gray-600 mt-2">Create and manage marketing campaigns with partner campaigns</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Campaigns"
          value={marketingCampaigns.length}
          icon={<Megaphone className="text-white" size={24} />}
          color="bg-purple-500"
        />
        <MetricCard
          title="Active Campaigns"
          value={marketingCampaigns.filter(c => c.status === 'active').length}
          icon={<Eye className="text-white" size={24} />}
          color="bg-green-500"
        />
        <MetricCard
          title="Draft Campaigns"
          value={marketingCampaigns.filter(c => c.status === 'draft').length}
          icon={<Edit className="text-white" size={24} />}
          color="bg-gray-500"
        />
        <MetricCard
          title="Partner Campaigns"
          value={partnerCampaigns.length}
          icon={<Link className="text-white" size={24} />}
          color="bg-blue-500"
        />
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-pink-700 transition duration-200 flex items-center"
        >
          <Plus size={20} className="mr-2" />
          New Campaign
        </button>
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Partner Campaigns
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Public Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCampaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-sm text-gray-500">{campaign.description}</div>
                      {campaign.region && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                            🌍 {getCountryNames([campaign.region])[0]} ({campaign.region})
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                      {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="flex flex-wrap gap-1">
                      {campaign.partner_campaign_ids.slice(0, 3).map(pcId => {
                        const pc = partnerCampaigns.find(c => c.id === pcId);
                        return pc ? (
                          <span key={pcId} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {pc.name}
                          </span>
                        ) : null;
                      })}
                      {campaign.partner_campaign_ids.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          +{campaign.partner_campaign_ids.length - 3} more
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {campaign.status === 'active' ? (
                      <div className="flex items-center space-x-2">
                        {campaign.id ? (
                          <>
                            <a
                              href={`/campaign/${campaign.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 flex items-center"
                              onClick={() => {
                                void debugLog('MarketingCampaignManager - Opening campaign link', {
                                  campaignId: campaign.id,
                                  campaignName: campaign.name
                                });
                              }}
                            >
                              <ExternalLink size={16} className="mr-1" />
                              View
                            </a>
                            <button
                              onClick={() => {
                                const url = getPublicUrl(campaign.id);
                                navigator.clipboard.writeText(url);
                                alert(`Link copied to clipboard:\n${url}`);
                              }}
                              className="text-gray-500 hover:text-gray-700"
                              title="Copy public link"
                            >
                              <Link size={16} />
                            </button>
                          </>
                        ) : (
                          <span className="text-red-500 text-sm">Campaign ID missing</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Not available</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {campaign.created_at 
                      ? new Date(campaign.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : 'N/A'
                    }
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => startEditCampaign(campaign)}
                        className="text-blue-600 hover:text-blue-800 transition duration-200"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await deleteMarketingCampaign(campaign.id);
                          } catch (error) {
                            console.error('Failed to delete marketing campaign:', error);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 transition duration-200"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Campaign Modal */}
      {(showCreateModal || editingCampaign) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingCampaign ? 'Edit Marketing Campaign' : 'Create New Marketing Campaign'}
            </h3>
            <form onSubmit={editingCampaign ? handleUpdateCampaign : handleCreateCampaign} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
                  <input
                    type="text"
                    value={campaignForm.name}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter campaign name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={campaignForm.status}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, status: e.target.value as MarketingCampaign['status'] }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              {/* Region Display */}
              {campaignForm.region && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Region</label>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <span className="inline-flex items-center text-blue-700 font-medium">
                      🌍 {getCountryNames([campaignForm.region])[0]} ({campaignForm.region})
                    </span>
                    <p className="text-xs text-blue-600 mt-1">
                      Region is automatically determined from selected partner campaigns
                    </p>
                  </div>
                </div>
              )}

              {/* Locale/Language Selector */}
              {campaignForm.region && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language/Locale (Optional)
                  </label>
                  <select
                    value={campaignForm.locale}
                    onChange={(e) => setCampaignForm(prev => ({ ...prev, locale: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Default (English)</option>
                    {campaignForm.region === 'USA' && (
                      <>
                        <option value="en-US">English (US)</option>
                        <option value="es-US">Spanish (US)</option>
                      </>
                    )}
                    {campaignForm.region === 'GBR' && (
                      <>
                        <option value="en-GB">English (UK)</option>
                      </>
                    )}
                    {campaignForm.region === 'CAN' && (
                      <>
                        <option value="en-CA">English (Canada)</option>
                        <option value="fr-CA">French (Canada)</option>
                      </>
                    )}
                    {campaignForm.region === 'FRA' && (
                      <>
                        <option value="fr-FR">French (France)</option>
                      </>
                    )}
                    {campaignForm.region === 'DEU' && (
                      <>
                        <option value="de-DE">German (Germany)</option>
                      </>
                    )}
                    {campaignForm.region === 'ESP' && (
                      <>
                        <option value="es-ES">Spanish (Spain)</option>
                      </>
                    )}
                    {campaignForm.region === 'BGR' && (
                      <>
                        <option value="bg">Bulgarian</option>
                      </>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the language for the public campaign page. This helps serve localized content to your audience.
                  </p>
                </div>
              )}
              
              <div className={campaignForm.region ? 'col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter campaign description"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Partner Campaigns ({campaignForm.partner_campaign_ids.length} selected)
                  </label>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={selectAllCampaigns}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={deselectAllCampaigns}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {getAvailablePartnerCampaigns().length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {campaignForm.region 
                        ? `No active partner campaigns available for ${getCountryNames([campaignForm.region])[0]} region.`
                        : 'No active partner campaigns available. Partner admins need to create and activate campaigns first.'
                      }
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {getAvailablePartnerCampaigns().map((pc) => (
                        <div key={pc.id} className="p-4 hover:bg-gray-50">
                          <div className="flex items-start space-x-3">
                            <button
                              type="button"
                              onClick={() => togglePartnerCampaign(pc.id)}
                              className="mt-1 text-blue-600 hover:text-blue-800"
                            >
                              {campaignForm.partner_campaign_ids.includes(pc.id) ? (
                                <CheckSquare size={20} />
                              ) : (
                                <Square size={20} />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <h4 className="text-sm font-medium text-gray-900">{pc.name}</h4>
                                <span className="text-xs text-gray-500">
                                  by {getPartnerName(pc.partner_id)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{pc.description}</p>
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                <span>Priority: {pc.priority}</span>
                                <span className={`px-2 py-1 rounded-full ${getStatusColor(pc.status)}`}>
                                  {pc.status}
                                </span>
                                {pc.region && (
                                  <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
                                    🌍 {getCountryNames([pc.region])[0]} ({pc.region})
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingCampaign(null);
                    resetCampaignForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg hover:from-purple-600 hover:to-pink-700 transition duration-200"
                >
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
  } catch (error) {
    console.error('MarketingCampaignManager render error:', error);
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <AlertTriangle size={48} className="mx-auto mb-2" />
            <h3 className="text-lg font-semibold">Error Loading Marketing Campaigns</h3>
          </div>
          <p className="text-gray-600 mb-4">There was an error loading the marketing campaigns.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }
};